import React from 'react'
import { Button, IconButton, useMediaQuery, useTheme } from '@mui/material'
import { J2Add } from '@e3dc-react/icons'
import { useNavigate } from 'react-router-dom'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useUser } from 'src/Hooks/useUser'
import { getCanCreateInstallations } from 'src/Libs/flow/utils/permissions'
import { isFlowTheme } from 'src/Libs/utils'
import { useUserRoles } from 'src/Hooks/user'
import useModals from 'src/Hooks/useModals'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'

/**
 * Button to create a new installation.
 * Only visible for Flow theme users with the appropriate permission level.
 */
const NewInstallationButton: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useCustomTranslation()
  const { userData } = useUser()
  const theme = useTheme()
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'))
  const { isServiceUser, isInstaller, isPartner } = useUserRoles()
  const { openNewInstallationDialog } = useModals()
  const {
    featureFlags: { isCreateInstallationEnabled },
  } = useFeatureFlagsContext()

  const canCreate = getCanCreateInstallations(userData as Parameters<typeof getCanCreateInstallations>[0])
  const hasE3dcAccess = (isServiceUser || (isInstaller && userData?.qntmCertified)) && isCreateInstallationEnabled

  const handleClick = (): void => {
    if (isFlowTheme) {
      navigate('/new-installation/general')
    } else {
      openNewInstallationDialog()
    }
  }

  if (isPartner || (isFlowTheme && !canCreate) || (!isFlowTheme && !hasE3dcAccess)) return null

  if (isSmDown) {
    return (
      <IconButton sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: 1 }} onClick={handleClick} data-qa="new-installation-button">
        <J2Add />
      </IconButton>
    )
  }
  return (
    <Button variant="contained" color="primary" onClick={handleClick} data-qa="new-installation-button" sx={{ whiteSpace: 'nowrap' }}>
      {t('installation.addInstallation')}
    </Button>
  )
}

export default NewInstallationButton
